import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { waLink, WA_MESSAGES } from "./data";
import { EASE } from "./Reveal";
import { scrollTo } from "./Nav";
import LeadForm from "./LeadForm";

const LINES = ["VIVE A LA ALTURA", "DE TU ESTILO", "DE VIDA"];

function WindCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);
    const N = 90;
    const parts = Array.from({ length: N }, () => ({
      x: Math.random() * 2000,
      y: Math.random(),
      s: 0.4 + Math.random() * 1.6,
      v: 0.4 + Math.random() * 1.4,
      o: 0.05 + Math.random() * 0.25,
      wob: Math.random() * Math.PI * 2,
    }));
    let t = 0;
    const draw = () => {
      t += 0.008;
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.x += p.v;
        if (p.x * devicePixelRatio > w + 20) p.x = -20;
        const y = (p.y + Math.sin(t * 2 + p.wob) * 0.012) * h;
        ctx.beginPath();
        ctx.arc(p.x * devicePixelRatio, y, p.s * devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(216, 196, 138, ${p.o})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />;
}

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#1c242f]"
    >
      <div className="absolute inset-0 spotlight" />
      <WindCanvas />

      <motion.div
        className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pt-28 sm:pt-36 pb-16 sm:pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
            className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-[#bfa360] mb-8"
          >
            Condominio Residencial · San Cristóbal Centro, Ecatepec
          </motion.p>

          <h1 className="font-display text-[13vw] sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "gold-text italic" : "text-[#f4f2ea]"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.7 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
            className="mt-7 max-w-md text-sm sm:text-base text-[#a8ac9b] leading-relaxed"
          >
            28 departamentos exclusivos de 2 a 3 recámaras, con roof gardens,
            eco elevador y acabados premium. Un proyecto de <span className="text-[#ddc68c]">PRODECOM · 25 aniversario</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.6, ease: EASE }}
            className="mt-6 flex flex-wrap items-center gap-2.5"
          >
            <span
              data-testid="hero-tag-precio"
              className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#1c242f] font-bold rounded-full px-4 py-2 bg-[#bfa360] shadow-[0_6px_24px_rgba(191,163,96,0.35)]"
            >
              Precios desde $2.4 mdp
            </span>
            {["Entrega inmediata", "Excelente ubicación", "Acabados de primera"].map((tag) => (
              <span
                key={tag}
                data-testid={`hero-tag-${tag.split(" ")[0].toLowerCase()}`}
                className="mono text-[10px] uppercase tracking-[0.2em] text-[#ddc68c] border border-[#bfa360]/40 rounded-full px-4 py-2 bg-[#bfa360]/10"
              >
                {tag}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.75, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-models"
              onClick={() => scrollTo("#departamentos")}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 text-sm text-[#f4f2ea] hover:border-[#bfa360]/60 hover:text-[#ddc68c] transition-colors"
            >
              Ver departamentos
              <ArrowDown size={16} />
            </button>
            <a
              data-testid="hero-cta-visit"
              href={waLink(WA_MESSAGES.visita)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2.5 text-sm text-[#ddc68c] link-line"
            >
              <MessageCircle size={16} />
              O escríbenos directo
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
        >
          <LeadForm
            idPrefix="hero-form"
            title="Agenda tu visita"
            subtitle="Cotización y disponibilidad hoy mismo."
            source="hero_form"
          />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 2.2 }}
        className="absolute bottom-0 right-0 z-10 hidden md:flex items-center gap-10 pr-8 lg:pr-16 pb-7 mono text-[11px] uppercase tracking-[0.25em] text-[#a8ac9b]"
      >
        <span><span className="text-[#ddc68c]">28</span> deptos</span>
        <span><span className="text-[#ddc68c]">7+PB</span> niveles</span>
        <span><span className="text-[#ddc68c]">3</span> roof gardens</span>
      </motion.div>
    </section>
  );
}
